import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Label } from "./ui/label";
import { useTranslation } from "react-i18next";

/**
 * Renders one chip-row per Tag Group whose `applies_to` matches the given
 * venue_type slug. After the unified-tags migration every group reads from
 * the `tags` collection (cuisines + dietary tags + everything else now
 * live there). Selections write to `tag_ids[]` which is persisted via the
 * /admin/venues/{id}/tags + /owner/venue/tags endpoints.
 *
 * Props:
 *  - venueTypeSlug  : current venue type slug ("" if none yet)
 *  - tagIds         : array of selected tag_ids
 *  - onTagToggle(id)
 */
export default function VenueTagChipGroups({
  venueTypeSlug,
  tagIds,
  onTagToggle,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [groups, setGroups] = useState([]);
  const [chipsByGroup, setChipsByGroup] = useState({});

  const fetchData = useCallback(async () => {
    if (!venueTypeSlug) {
      setGroups([]);
      setChipsByGroup({});
      return;
    }
    try {
      const grpRes = await axios.get(`${API}/taxonomy/tag-groups?venue_type=${venueTypeSlug}`);
      const allGroups = grpRes.data || [];
      setGroups(allGroups);
      // For each group, fetch tags scoped to that group_slug. We still apply
      // a per-tag applies_to filter client-side so cuisines like "Cypriot"
      // (applies_to=[restaurant,taverna]) don't surface on a bar venue type.
      const tagsRes = await axios.get(`${API}/taxonomy/tags?public_only=false`);
      const allTags = tagsRes.data || [];
      const map = {};
      for (const grp of allGroups) {
        map[grp.slug] = allTags
          .filter((tag) => {
            if (tag.tag_group_slug !== grp.slug) return false;
            if (tag.is_active === false) return false;
            const at = tag.applies_to;
            if (!at || at.length === 0) return true;
            return at.includes(venueTypeSlug);
          })
          .map((tag) => ({
            id: tag.tag_id, slug: tag.slug, name: tag.name, name_el: tag.name_el || "",
          }));
      }
      setChipsByGroup(map);
    } catch {
      // ignore
    }
  }, [venueTypeSlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const localized = useCallback((item) => {
    if (!item) return "";
    return (lang === "el" && item.name_el) || item.name || "";
  }, [lang]);

  if (!venueTypeSlug) {
    return (
      <div>
        <p className="text-xs text-muted-foreground italic">
          {t("admin_venue_edit:select_type_first", { defaultValue: "Select type first" })}
        </p>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <p className="text-xs text-muted-foreground italic">
        {t("admin_venue_edit:no_tag_groups", { defaultValue: "No tag groups for this venue type. Add some via Admin → Tag Groups." })}
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="venue-tag-chip-groups">
      {groups.map((grp) => {
        const heading = (lang === "el" && grp.name_el) || grp.name;
        const chips = chipsByGroup[grp.slug] || [];
        const selectedSet = new Set(tagIds || []);
        return (
          <div key={grp.group_id} data-testid={`edit-venue-chip-group-${grp.slug}`}>
            <Label className="text-sm font-medium">{heading}</Label>
            {chips.length === 0 ? (
              <p className="text-xs text-muted-foreground italic mt-2">
                {t("admin_venue_edit:no_chips_in_group", { defaultValue: "No options yet — add some via Admin → Taxonomy." })}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {chips.map((chip) => {
                  const active = selectedSet.has(chip.id);
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() => onTagToggle?.(chip.id)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 text-foreground border-border hover:bg-muted"}`}
                      data-testid={`edit-venue-chip-${grp.slug}-${chip.slug}`}
                    >
                      {localized(chip)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
